import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import { Note } from './peopleUI'
import './people.css'

/*
 * Open a scout (manager) account from the back office.
 *
 * Until now the only way to get a manager account was to buy one. Two kinds
 * can be opened here:
 *
 *   Full access for N days   any number of days — a client who paid another
 *                            way, a colleague, a demo account
 *   Free trial, 24 hours     the same product without saving lists,
 *                            messaging players or exporting; when it ends the
 *                            scout is sent to the plans
 *
 * The plan sets the sport, the plan name the scout sees, and how many
 * countries are included. A trial becomes a full account later from the list
 * below: give it an end date and it has full access until that day.
 *
 * Same pattern as adding a player: the back office sets the sign-in details.
 */

const api = () => process.env.REACT_APP_API_SERVER
const auth = () => ({ headers: { Authorization: 'Bearer ' + sessionStorage.getItem('apiToken') } })
const rowsOf = (res) => (res && res.data && Array.isArray(res.data.results)
  ? res.data.results
  : Array.isArray(res && res.data) ? res.data : [])

const EMPTY = { FullName: '', Email: '', Password: '', ProductID: '', mode: 'days', days: '30' }

export default function NewScoutForm({ onCreated, onCancel }) {
  const [products, setProducts] = useState([])
  const [countries, setCountries] = useState([])
  const [loadFailed, setLoadFailed] = useState(false)

  const [f, setF] = useState(EMPTY)
  const [picked, setPicked] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [created, setCreated] = useState(null)

  useEffect(() => {
    Promise.all([
      axios.get(api() + 'admin/eshopproducts', auth()),
      axios.get(api() + 'admin/countries', auth())
    ]).then(([p, c]) => {
      setProducts(rowsOf(p))
      setCountries(rowsOf(c).filter(row => row && row.CountryID).sort((a, b) => String(a.Country).localeCompare(String(b.Country))))
    }).catch(() => setLoadFailed(true))
  }, [])

  const set = (key) => (e) => setF(prev => ({ ...prev, [key]: e.target.value }))

  const product = useMemo(
    () => products.find(p => String(p.id) === String(f.ProductID)) || null,
    [products, f.ProductID]
  )
  const allowed = product ? Number(product.NumberOfCountries) : 0
  const allOfEurope = !!product && allowed === 0

  /* A smaller plan can hold fewer countries; drop the ones that no longer fit. */
  useEffect(() => {
    if (!product) return
    setPicked(prev => (allOfEurope ? [] : prev.slice(0, allowed)))
  }, [product, allOfEurope, allowed])

  const toggle = (id) => setPicked(prev => {
    if (prev.indexOf(id) > -1) return prev.filter(x => x !== id)
    if (prev.length >= allowed) return prev
    return [...prev, id]
  })

  const bySport = useMemo(() => {
    const groups = {}
    products.forEach(p => { (groups[p.SportName || 'Other'] = groups[p.SportName || 'Other'] || []).push(p) })
    return groups
  }, [products])

  const submit = (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    axios.post(api() + 'admin/eshopscustomers/create', {
      FullName: f.FullName,
      Email: f.Email,
      Password: f.Password,
      ProductID: f.ProductID,
      countries: picked,
      mode: f.mode,
      days: f.days
    }, auth()).then(res => {
      setBusy(false)
      if (res.data && res.data.err === 0) {
        setCreated({
          name: f.FullName,
          email: res.data.email,
          plan: res.data.plan,
          trial: res.data.mode === 'trial',
          endsAt: res.data.endsAt
        })
        setF(EMPTY)
        setPicked([])
        if (onCreated) onCreated()
      } else {
        setError((res.data && res.data.msg) || 'The account could not be created.')
      }
    }).catch(() => {
      setBusy(false)
      setError('The account could not be created. Check your connection and try again.')
    })
  }

  const until = (c) => (c.trial
    ? new Date(c.endsAt).toLocaleString('en-GB', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
    : new Date(c.endsAt + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }))

  return (
    <Panel title="New Scout Account" subtitle="Full access for a set number of days, or a 24-hour free trial">
      <form className="pp-form" onSubmit={submit}>

        {loadFailed ? (
          <Note tone="crit">The plans or countries could not be loaded. Close this form and try again.</Note>
        ) : null}

        {created ? (
          <Note tone="good" onDismiss={() => setCreated(null)}>
            <b>{created.name}</b> can now sign in to the manager with <b>{created.email}</b> and the password
            you set — {created.plan}, {created.trial ? 'free trial until ' : 'full access until '}{until(created)}.
          </Note>
        ) : null}

        {error ? <Note tone="crit" onDismiss={() => setError('')}>{error}</Note> : null}

        <fieldset className="pp-fieldset">
          <legend className="pp-legend">Scout</legend>
          <div className="pp-grid">
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="ns-name">Full name</label>
              <input id="ns-name" className="pp-input" required minLength={2} maxLength={120} value={f.FullName} onChange={set('FullName')} />
            </div>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="ns-email">Email</label>
              <input id="ns-email" className="pp-input" type="email" required maxLength={200} autoComplete="off" value={f.Email} onChange={set('Email')} />
              <span className="pp-hint">Used to sign in to the manager.</span>
            </div>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="ns-pass">Password</label>
              <input id="ns-pass" className="pp-input" type="text" required minLength={8} maxLength={64} autoComplete="off" value={f.Password} onChange={set('Password')} />
              <span className="pp-hint">8 characters or more. The scout can change it from their profile.</span>
            </div>
          </div>
        </fieldset>

        <fieldset className="pp-fieldset">
          <legend className="pp-legend">Plan</legend>
          <div className="pp-grid">
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="ns-plan">Plan</label>
              <select id="ns-plan" className="pp-select" required value={f.ProductID} onChange={set('ProductID')}>
                <option value="">Choose a plan</option>
                {Object.keys(bySport).map(sport => (
                  <optgroup key={sport} label={sport}>
                    {bySport[sport].map(p => (
                      <option key={p.id} value={p.id}>
                        {p.Title} — {Number(p.NumberOfCountries) === 0 ? 'all of Europe' : p.NumberOfCountries + (Number(p.NumberOfCountries) === 1 ? ' country' : ' countries')}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span className="pp-hint">Sets the sport and how many countries are included.</span>
            </div>

            <div className="pp-field">
              <span className="pp-label" data-req="1">Access</span>
              <label className="ns-choice">
                <input type="radio" name="ns-mode" value="days" checked={f.mode === 'days'} onChange={set('mode')} />
                <span>Full access for</span>
                <input
                  className="pp-input ns-days"
                  type="number"
                  min={1}
                  max={3650}
                  value={f.days}
                  onChange={set('days')}
                  disabled={f.mode !== 'days'}
                  aria-label="Number of days"
                />
                <span>days</span>
              </label>
              <label className="ns-choice">
                <input type="radio" name="ns-mode" value="trial" checked={f.mode === 'trial'} onChange={set('mode')} />
                <span>Free trial, 24 hours</span>
              </label>
              <span className="pp-hint">
                {f.mode === 'trial'
                  ? 'No saving lists, messaging players or exporting. When it ends the scout is shown the plans.'
                  : 'Everything the plan includes, until the end of the last day.'}
              </span>
            </div>
          </div>

          {product ? (
            allOfEurope ? (
              <p className="pp-hint">This plan includes every country.</p>
            ) : (
              <div className="pp-field">
                <span className="pp-label" data-req="1">
                  Countries — {picked.length} of {allowed}
                </span>
                <div className="ns-countries">
                  {countries.map(c => {
                    const id = Number(c.CountryID)
                    const on = picked.indexOf(id) > -1
                    const full = !on && picked.length >= allowed
                    return (
                      <label key={id} className="ns-country" data-off={full ? '1' : undefined}>
                        <input type="checkbox" checked={on} disabled={full} onChange={() => toggle(id)} />
                        <span>{c.Country}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            )
          ) : null}
        </fieldset>

        <div className="pp-actions">
          <Btn onClick={onCancel}>Back to the list</Btn>
          <Btn
            variant="accent"
            type="submit"
            disabled={busy || loadFailed || (!!product && !allOfEurope && picked.length === 0)}
          >
            {busy ? 'Creating…' : 'Create account'}
          </Btn>
        </div>
      </form>
    </Panel>
  )
}
